import React, { useEffect, useRef } from 'react';

interface DustParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  life: number;
  maxLife: number;
  color: string;
}

interface BlueDustAnimationProps {
  className?: string;
}

const BlueDustAnimation: React.FC<BlueDustAnimationProps> = ({ className = '' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<DustParticle[]>([]);
  const animationRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Устанавливаем размер canvas равным размеру родительского элемента
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Цвета для частиц пыли (синие оттенки под цвет текста)
    const dustColors = [
      '#60a5fa', // Синий
      '#3b82f6', // Синий
      '#2563eb', // Синий
      '#1d4ed8', // Темно-синий
      '#1e40af', // Темно-синий
      '#1e3a8a', // Очень темно-синий
    ];

    // Создаем частицы пыли
    const createParticles = () => {
      const particles: DustParticle[] = [];
      const particleCount = 15; // Уменьшил количество для более плавной анимации

      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.2, // Уменьшил скорость
          vy: (Math.random() - 0.5) * 0.15,
          size: Math.random() * 2 + 0.8,
          opacity: 0.6, // Фиксированная прозрачность
          life: Math.random() * 200, // Увеличил время жизни
          maxLife: 300 + Math.random() * 200,
          color: dustColors[Math.floor(Math.random() * dustColors.length)]
        });
      }
      return particles;
    };

    particlesRef.current = createParticles();

    // Функция анимации
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particlesRef.current.forEach((particle, index) => {
        // Обновляем позицию
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Обновляем жизнь
        particle.life += 0.5;

        // Если частица умерла, создаем новую с задержкой
        if (particle.life > particle.maxLife) {
          // Создаем новую частицу в случайном месте, но с начальной прозрачностью 0
          particlesRef.current[index] = {
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            vx: (Math.random() - 0.5) * 0.2,
            vy: (Math.random() - 0.5) * 0.15,
            size: Math.random() * 2 + 0.8,
            opacity: 0.6,
            life: -50, // Отрицательная жизнь для плавного появления
            maxLife: 300 + Math.random() * 200,
            color: dustColors[Math.floor(Math.random() * dustColors.length)]
          };
        }

        // Ограничиваем движение в пределах canvas с плавным отскоком
        if (particle.x < 0) {
          particle.x = 0;
          particle.vx = Math.abs(particle.vx) * 0.8;
        } else if (particle.x > canvas.width) {
          particle.x = canvas.width;
          particle.vx = -Math.abs(particle.vx) * 0.8;
        }
        
        if (particle.y < 0) {
          particle.y = 0;
          particle.vy = Math.abs(particle.vy) * 0.8;
        } else if (particle.y > canvas.height) {
          particle.y = canvas.height;
          particle.vy = -Math.abs(particle.vy) * 0.8;
        }

        // Рисуем частицу с плавным появлением и исчезновением
        const lifeProgress = particle.life / particle.maxLife;
        let alpha = particle.opacity;
        
        // Если частица еще не родилась (отрицательная жизнь), не рисуем её
        if (particle.life < 0) {
          return;
        }
        
        // Плавное появление в первые 10% жизни
        if (lifeProgress < 0.1) {
          alpha = particle.opacity * (lifeProgress / 0.1);
        }
        // Плавное исчезновение в последние 10% жизни
        else if (lifeProgress > 0.9) {
          alpha = particle.opacity * ((1 - lifeProgress) / 0.1);
        }
        
        // Не рисуем частицы с очень низкой прозрачностью
        if (alpha < 0.05) {
          return;
        }
        
        ctx.save();
        ctx.globalAlpha = alpha;
        
        // Создаем градиент для частицы
        const gradient = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.size
        );
        gradient.addColorStop(0, particle.color);
        gradient.addColorStop(0.7, particle.color + '80');
        gradient.addColorStop(1, 'transparent');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
        
        // Добавляем мягкое свечение
        ctx.shadowColor = particle.color;
        ctx.shadowBlur = particle.size * 1.5;
        ctx.fillStyle = particle.color;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 0.4, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.restore();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{ zIndex: 1 }}
    />
  );
};

export default BlueDustAnimation; 